import React, { memo, useMemo } from 'react';
import { useSortable } from '@dnd-kit/react/sortable';
import { formatTestCardId } from '../../../utils/converter';
import { cardSignal, ownerInitials, ownerName, structureOf } from '../epmHelpers';

export const Item = memo(function Item({ id, index, column, test, tagsMap, onClick, disabled = false }) {
    const { ref, isDragging } = useSortable({
        id,
        index,
        type: 'item',
        accept: 'item',
        group: column,
        disabled,
    });

    const firstTagName = useMemo(() => {
        const tagId = test?.tag_ids?.[0];
        return tagId && tagsMap?.[tagId]?.name ? tagsMap[tagId].name : null;
    }, [test?.tag_ids, tagsMap]);

    const authorNameStr = ownerName(test);
    const initials = ownerInitials(test);
    const cardId = formatTestCardId(test || id);
    const signal = cardSignal(test);
    const isFlagged = Boolean(structureOf(test).flag);

    return (
        <button
            ref={ref}
            type="button"
            onClick={onClick}
            data-dragging={isDragging}
            title={authorNameStr}
            className={`board__card w-full text-left bg-white border border-border rounded-xl px-3.5 py-3 flex flex-col gap-2.5 ${
                disabled ? 'cursor-pointer' : 'cursor-grab active:cursor-grabbing'
            } select-none touch-none hover:border-border-hover hover:shadow-card-hover transition-all shadow-xs ${
                isFlagged ? 'border-l-4 border-l-amber-500' : ''
            } ${isDragging ? 'opacity-40 shadow-lg ring-2 ring-primary/30' : ''}`}
        >
            {/* Card Key */}
            <span className="font-mono text-[11px] font-semibold tracking-wider text-gray-400 uppercase">
                {cardId}
            </span>

            {/* Test Title */}
            <span className="text-xs md:text-sm font-semibold text-gray-900 leading-snug line-clamp-2">
                {test?.name ?? id}
            </span>

            {/* Card Metadata / Footer */}
            <div className="flex items-center gap-2 text-xs text-gray-500 pt-1 flex-wrap border-t border-gray-50 mt-0.5">
                <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary-soft text-primary text-[10px] font-bold flex-none">
                    {initials}
                </span>

                {firstTagName && (
                    <span className="px-2 py-0.5 rounded-md bg-slate-50 border border-slate-200/70 text-gray-600 text-[11px] font-medium whitespace-nowrap truncate max-w-[110px]">
                        {firstTagName}
                    </span>
                )}

                {signal && (
                    <span className={`ml-auto text-[11px] whitespace-nowrap font-medium ${signal.tone === 'warn' ? 'text-amber-600 font-semibold' : 'text-gray-400'}`}>
                        {signal.text}
                    </span>
                )}
            </div>
        </button>
    );
});
